import { accelerationTiltDegrees, effectiveScreenOrientationAngle, screenTiltDegrees, smoothTiltDegrees } from './device.js';
import {
  calibrateTiltSamples,
  calibratedTiltSteering,
  defaultTiltCalibration,
  loadTiltCalibration,
  saveTiltCalibration,
  type TiltCalibrationProfile,
} from './tilt-calibration.js';

/**
 * Motion access and a lightweight live tilt reader for the menu. The driver
 * step grants sensor permission from a real tap (iOS requires that) and lets
 * the player feel and centre tilt steering before the first race, so the race
 * itself never has to interrupt with a permission dialog.
 */

export type MotionAccess = 'unknown' | 'granted' | 'denied' | 'insecure' | 'missing';

type PermissionRequest = () => Promise<'granted' | 'denied'>;
type SensorWindow = Window & {
  DeviceOrientationEvent?: { requestPermission?: PermissionRequest };
  DeviceMotionEvent?: { requestPermission?: PermissionRequest };
};

let access: MotionAccess = 'unknown';

export function motionAccess(): MotionAccess {
  return access;
}

/** True when this page session already holds sensor permission. */
export function motionAccessGranted(): boolean {
  return access === 'granted';
}

export function motionNeedsPermissionTap(): boolean {
  const sensorWindow = window as SensorWindow;
  return typeof sensorWindow.DeviceOrientationEvent?.requestPermission === 'function'
    || typeof sensorWindow.DeviceMotionEvent?.requestPermission === 'function';
}

/** Must be called synchronously from a tap handler. */
export async function requestMotionAccess(): Promise<MotionAccess> {
  if (!window.isSecureContext) return (access = 'insecure');
  const sensorWindow = window as SensorWindow;
  const hasOrientation = 'DeviceOrientationEvent' in window || 'ondeviceorientation' in window;
  const hasMotion = 'DeviceMotionEvent' in window || 'ondevicemotion' in window;
  if (!hasOrientation && !hasMotion) return (access = 'missing');
  const requests: Promise<'granted' | 'denied'>[] = [];
  // Start both requests inside the same tap; awaiting one first loses the
  // user activation on some Safari versions.
  for (const target of [sensorWindow.DeviceOrientationEvent, sensorWindow.DeviceMotionEvent]) {
    const request = target?.requestPermission;
    if (typeof request !== 'function') continue;
    try {
      requests.push(Promise.resolve(request.call(target)));
    } catch {
      requests.push(Promise.resolve('denied'));
    }
  }
  if (requests.length === 0) return (access = 'granted');
  const results = await Promise.allSettled(requests);
  access = results.some((result) => result.status === 'fulfilled' && result.value === 'granted') ? 'granted' : 'denied';
  return access;
}

export interface TiltPreviewSample {
  /** −1…1, positive = left (shared control convention). */
  steer: number;
  tilt: number;
  neutral: number;
}

/** Reads the sensor without a running race. */
export class TiltPreview {
  private listening = false;
  private tilt = Number.NaN;
  private lastAt = 0;
  private samples: number[] = [];
  private calibration: TiltCalibrationProfile | null = null;
  private orientationAngle = 0;
  private lastOrientationEventAt = 0;
  private stallTimer: number | undefined;
  sensitivity = 1;
  deadZone = 2;

  constructor(
    private readonly onSample: (sample: TiltPreviewSample) => void,
    private readonly onStall: () => void,
  ) {}

  start(): void {
    if (this.listening) return;
    this.listening = true;
    window.addEventListener('deviceorientation', this.onOrientation, true);
    window.addEventListener('devicemotion', this.onMotion, true);
    this.stallTimer = window.setTimeout(() => {
      if (!Number.isFinite(this.tilt)) this.onStall();
    }, 2_500);
  }

  stop(): void {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener('deviceorientation', this.onOrientation, true);
    window.removeEventListener('devicemotion', this.onMotion, true);
    if (this.stallTimer !== undefined) window.clearTimeout(this.stallTimer);
    this.stallTimer = undefined;
  }

  get live(): boolean {
    return Number.isFinite(this.tilt) && performance.now() - this.lastAt < 1_000;
  }

  /** Saves "how I hold it" as the centre for the race's input layer too. */
  calibrate(): boolean {
    if (!Number.isFinite(this.tilt)) return false;
    this.calibration = calibrateTiltSamples(this.samples.length ? this.samples.slice(-24) : [this.tilt], this.calibration ?? undefined);
    saveTiltCalibration(this.orientationAngle, this.calibration);
    this.emit();
    return true;
  }

  private readonly onOrientation = (raw: Event): void => {
    const event = raw as DeviceOrientationEvent;
    const beta = Number(event.beta);
    const gamma = Number(event.gamma);
    if (!Number.isFinite(beta) && !Number.isFinite(gamma)) return;
    this.lastOrientationEventAt = performance.now();
    this.accept(screenTiltDegrees(Number.isFinite(beta) ? beta : 0, Number.isFinite(gamma) ? gamma : 0, this.angle()));
  };

  private readonly onMotion = (raw: Event): void => {
    // Orientation is preferred; gravity is only a fallback for old tablets.
    if (performance.now() - this.lastOrientationEventAt < 500) return;
    const gravity = (raw as DeviceMotionEvent).accelerationIncludingGravity;
    if (!gravity) return;
    const tilt = accelerationTiltDegrees(Number(gravity.x), Number(gravity.y), Number(gravity.z), this.angle());
    if (Number.isFinite(tilt)) this.accept(tilt);
  };

  private accept(rawTilt: number): void {
    const now = performance.now();
    const dt = this.lastAt ? (now - this.lastAt) / 1_000 : 0.016;
    this.tilt = Number.isFinite(this.tilt) ? smoothTiltDegrees(this.tilt, rawTilt, dt) : rawTilt;
    this.lastAt = now;
    this.samples.push(this.tilt);
    if (this.samples.length > 60) this.samples.shift();
    const angle = this.angle();
    if (!this.calibration || angle !== this.orientationAngle) {
      this.orientationAngle = angle;
      const stored = loadTiltCalibration(angle);
      this.calibration = stored ?? defaultTiltCalibration(this.tilt);
    }
    this.emit();
  }

  private emit(): void {
    if (!this.calibration || !Number.isFinite(this.tilt)) return;
    this.onSample({
      steer: calibratedTiltSteering(this.tilt, this.calibration, this.sensitivity, this.deadZone),
      tilt: this.tilt,
      neutral: this.calibration.neutral,
    });
  }

  private angle(): number {
    const legacyAngle = (window as Window & { orientation?: number }).orientation;
    return effectiveScreenOrientationAngle(
      window.screen.orientation?.angle,
      window.screen.orientation?.type,
      window.matchMedia('(orientation: landscape)').matches,
      legacyAngle,
    );
  }
}
